/**
 * Staff API Service for Wadakaroo
 * Handles all staff-related API calls.
 */

import axiosInstance from './axiosInstance';

// ============================================
// Staff CRUD Endpoints
// ============================================

/**
 * Get paginated list of staff members
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (0-based)
 * @param {number} params.size - Page size
 * @param {boolean} params.activeOnly - Filter active staff only
 * @param {string} params.search - Search term for name/email
 */
export const getStaffListApi = async ({ page = 0, size = 20, activeOnly = false, search = '' } = {}) => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('size', size.toString());
    params.append('activeOnly', activeOnly.toString());
    if (search) {
        params.append('search', search);
    }

    const response = await axiosInstance.get(`/staff?${params.toString()}`);
    return response.data;
};

/**
 * Get a single staff member by UUID
 * @param {string} uuid - Staff UUID
 */
/**
 * Who is signed in, by name.
 *
 * 🔑 The token cannot answer this. It carries staffUuid and the sign-in email and no
 * name at all — which is why the sidebar shows an email address. Asked for at the one
 * moment it is needed rather than added as a token claim, which every request in flight
 * would then carry and which would go stale the moment somebody is renamed.
 *
 * Needs no staff:read: that is a permission over OTHER people's records, and seeing
 * yourself is not staff administration. Takes no argument, so it cannot be pointed at
 * anybody else.
 */
export const getMyStaffApi = async () => {
    const response = await axiosInstance.get('/staff/me');
    return response.data;
};

export const getStaffApi = async (uuid) => {
    const response = await axiosInstance.get(`/staff/${uuid}`);
    return response.data;
};

/**
 * Create a new staff member
 * @param {Object} staffData - Staff creation data
 */
export const createStaffApi = async (staffData) => {
    const response = await axiosInstance.post('/staff', staffData);
    return response.data;
};

/**
 * Update an existing staff member
 * @param {string} uuid - Staff UUID
 * @param {Object} staffData - Staff update data
 */
export const updateStaffApi = async (uuid, staffData) => {
    const response = await axiosInstance.put(`/staff/${uuid}`, staffData);
    return response.data;
};

/**
 * Delete (deactivate) a staff member
 * @param {string} uuid - Staff UUID
 */
export const deleteStaffApi = async (uuid) => {
    const response = await axiosInstance.delete(`/staff/${uuid}`);
    return response.data;
};

/**
 * Resend welcome email to a staff member
 * @param {string} uuid - Staff UUID
 */
export const resendWelcomeEmailApi = async (uuid) => {
    const response = await axiosInstance.post(`/staff/${uuid}/resend-welcome`);
    return response.data;
};

// ============================================
// Document Endpoints
// ============================================

/**
 * Get documents for a staff member
 * @param {string} staffUuid - Staff UUID
 */
export const getStaffDocumentsApi = async (staffUuid) => {
    const response = await axiosInstance.get(`/staff/${staffUuid}/documents`);
    return response.data;
};

/**
 * Upload a document for a staff member
 * @param {string} staffUuid - Staff UUID
 * @param {File} file - File to upload
 * @param {string} documentType - Type of document
 * @param {string} documentName - Display name for the document
 * @param {string} notes - Optional notes
 */
export const uploadStaffDocumentApi = async (staffUuid, file, documentType, documentName = '', notes = '') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    formData.append('documentName', documentName || file.name);
    formData.append('notes', notes);

    const response = await axiosInstance.post(`/staff/${staffUuid}/documents`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

/**
 * Delete a document
 * @param {string} staffUuid - Staff UUID
 * @param {string} documentUuid - Document UUID
 */
export const deleteStaffDocumentApi = async (staffUuid, documentUuid) => {
    const response = await axiosInstance.delete(`/staff/${staffUuid}/documents/${documentUuid}`);
    return response.data;
};

// ============================================
// Leave Plan Endpoints
// ============================================

/**
 * Get leave plans for a staff member
 * @param {string} staffUuid - Staff UUID
 */
export const getStaffLeavePlansApi = async (staffUuid) => {
    const response = await axiosInstance.get(`/staff/${staffUuid}/leave-plans`);
    return response.data;
};

/**
 * Create a leave plan for a staff member
 * @param {string} staffUuid - Staff UUID
 * @param {Object} leaveData - Leave plan data
 * @param {string} leaveData.startDate - ISO date string (YYYY-MM-DD)
 * @param {string} leaveData.endDate - ISO date string (YYYY-MM-DD)
 */
export const createStaffLeavePlanApi = async (staffUuid, leaveData) => {
    const response = await axiosInstance.post(`/staff/${staffUuid}/leave-plans`, leaveData);
    return response.data;
};

/**
 * Update a leave plan
 * @param {string} staffUuid - Staff UUID
 * @param {string} leavePlanUuid - Leave plan UUID
 * @param {Object} leaveData - Leave plan data
 */
export const updateStaffLeavePlanApi = async (staffUuid, leavePlanUuid, leaveData) => {
    const response = await axiosInstance.put(`/staff/${staffUuid}/leave-plans/${leavePlanUuid}`, leaveData);
    return response.data;
};

/**
 * Delete a leave plan
 * @param {string} staffUuid - Staff UUID
 * @param {string} leavePlanUuid - Leave plan UUID
 */
export const deleteStaffLeavePlanApi = async (staffUuid, leavePlanUuid) => {
    const response = await axiosInstance.delete(`/staff/${staffUuid}/leave-plans/${leavePlanUuid}`);
    return response.data;
};

/**
 * Get leave plans for a staff member that overlap a given date window.
 * @param {string} staffUuid - Staff UUID
 * @param {string} from - ISO date (YYYY-MM-DD), inclusive
 * @param {string} to - ISO date (YYYY-MM-DD), inclusive
 */
export const getStaffLeavePlansInRangeApi = async (staffUuid, from, to) => {
    const params = new URLSearchParams({ from, to });
    const response = await axiosInstance.get(
        `/staff/${staffUuid}/leave-plans/in-range?${params.toString()}`
    );
    return response.data;
};

/**
 * Get all leave plans across the current organisation that overlap the given
 * window. Returns a flat list of { uuid, staffUuid, startDate, endDate }.
 * Used by the resource planner and the staff assignment modal.
 * @param {string} from - ISO date (YYYY-MM-DD), inclusive
 * @param {string} to - ISO date (YYYY-MM-DD), inclusive
 */
export const getAllStaffLeavePlansInRangeApi = async (from, to) => {
    const params = new URLSearchParams({ from, to });
    const response = await axiosInstance.get(`/staff/leave-plans/in-range?${params.toString()}`);
    return response.data;
};

// ============================================
// Utility Endpoints
// ============================================

/**
 * Get available staff roles
 */
export const getStaffRolesApi = async () => {
    const response = await axiosInstance.get('/staff/roles');
    return response.data;
};

/**
 * Get available document types
 */
export const getDocumentTypesApi = async () => {
    const response = await axiosInstance.get('/staff/document-types');
    return response.data;
};

/**
 * Get assignable profiles (excludes Owner profile)
 */
export const getAssignableProfilesApi = async () => {
    const response = await axiosInstance.get('/profiles/assignable');
    return response.data;
};

/**
 * Get UI View Profiles for the current organisation
 */
export const getUiViewProfilesApi = async () => {
    const response = await axiosInstance.get('/ui-view-profiles');
    return response.data;
};

// ============================================
// Default Export
// ============================================

const staffApi = {
    getStaffListApi,
    getStaffApi,
    createStaffApi,
    updateStaffApi,
    deleteStaffApi,
    resendWelcomeEmailApi,
    getStaffDocumentsApi,
    uploadStaffDocumentApi,
    deleteStaffDocumentApi,
    getStaffLeavePlansApi,
    createStaffLeavePlanApi,
    updateStaffLeavePlanApi,
    deleteStaffLeavePlanApi,
    getStaffLeavePlansInRangeApi,
    getAllStaffLeavePlansInRangeApi,
    getStaffRolesApi,
    getDocumentTypesApi,
    getAssignableProfilesApi,
    getUiViewProfilesApi,
};

export default staffApi;